/**
 * Histórico de impressão dos apontamentos (ERP, por unidade da sessão).
 *
 *   GET  /api/apontamentos/resumo       → vias e última impressão por lote+setor
 *   GET  /api/apontamentos/impressoes   → lista para a aba Histórico
 *   POST /api/apontamentos/impressoes   → registra ao abrir o diálogo de impressão
 *
 * O navegador não conta se a pessoa cancelou o diálogo de impressão: o
 * registro é "enviado para impressão" (especificação, seção 8).
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../../context/AuthContext";
import { indexarResumo } from "../../utils/apontamentos";

const BASE = "/api/apontamentos";
const LIMITE_DO_HISTORICO = 200;

export default function useHistoricoImpressoes() {
  const { request } = useAuth();
  const [resumo, setResumo] = useState([]);
  const [carregado, setCarregado] = useState(false);
  const [erro, setErro] = useState(null);

  const recarregar = useCallback(async () => {
    try {
      const resposta = await request(`${BASE}/resumo`, { silentError: true });
      setResumo(Array.isArray(resposta?.itens) ? resposta.itens : []);
      setErro(null);
    } catch (falha) {
      setErro(falha?.message || "Não foi possível ler o histórico de impressão.");
    } finally {
      setCarregado(true);
    }
  }, [request]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  const resumoPorLote = useMemo(() => indexarResumo(resumo), [resumo]);

  /**
   * Registra as folhas que vão sair e devolve a via de cada lote+setor
   * ({ "chave|setor": via }). Falha sobe para quem chamou: sem registro
   * não há marca de 2ª via confiável.
   */
  const registrar = useCallback(async (formato, itens) => {
    // silentError: a tela pergunta se sai mesmo assim (sem toast duplicado).
    const resposta = await request(`${BASE}/impressoes`, {
      method: "POST",
      body: JSON.stringify({ formato, itens }),
      silentError: true,
    });
    await recarregar();
    return Object.fromEntries((resposta?.itens || []).map((item) => [`${item.lote_chave}|${item.setor}`, item.via]));
  }, [request, recarregar]);

  const listar = useCallback(
    () => request(`${BASE}/impressoes?limite=${LIMITE_DO_HISTORICO}`, { silentError: true }).then((r) => r?.itens || []),
    [request],
  );

  return { resumoPorLote, carregado, erro, recarregar, registrar, listar };
}
